/**
 * 护理完成模态框
 * 收集最终结果反馈
 * Requirements: 7.5
 */

import { useState } from "react";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Heart,
  TrendingUp,
  Minus,
  Frown,
  HelpCircle,
  PartyPopper,
} from "lucide-react";

type CareOutcome = "recovered" | "improved" | "unchanged" | "lost" | "unknown";

interface OutcomeOption {
  value: CareOutcome;
  label: string;
  description: string;
  icon: typeof Heart;
  color: string;
  bgColor: string;
}

const outcomeOptions: OutcomeOption[] = [
  {
    value: "recovered",
    label: "完全恢复",
    description: "症状已完全消失",
    icon: Heart,
    color: "text-green-600",
    bgColor: "bg-green-50",
  },
  {
    value: "improved",
    label: "明显好转",
    description: "症状有明显改善",
    icon: TrendingUp,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  {
    value: "unchanged",
    label: "无明显变化",
    description: "症状基本保持不变",
    icon: Minus,
    color: "text-amber-600",
    bgColor: "bg-amber-50",
  },
  {
    value: "lost",
    label: "不幸离世",
    description: "很抱歉听到这个消息",
    icon: Frown,
    color: "text-gray-600",
    bgColor: "bg-gray-50",
  },
  {
    value: "unknown",
    label: "不确定",
    description: "无法判断结果",
    icon: HelpCircle,
    color: "text-gray-500",
    bgColor: "bg-gray-50",
  },
];

interface CareCompletionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete: (outcome: CareOutcome, feedback?: string) => Promise<void>;
  careDurationDays: number;
  isSubmitting?: boolean;
}

export function CareCompletionModal({
  open,
  onOpenChange,
  onComplete,
  careDurationDays,
  isSubmitting = false,
}: CareCompletionModalProps) {
  const [selectedOutcome, setSelectedOutcome] = useState<CareOutcome | null>(
    null
  );
  const [feedback, setFeedback] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);

  const handleComplete = async () => {
    if (!selectedOutcome) return;
    await onComplete(selectedOutcome, feedback.trim() || undefined);
    // 重置状态
    setSelectedOutcome(null);
    setFeedback("");
    setShowFeedback(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <PartyPopper className="w-6 h-6 text-amber-500" />
            <DialogTitle>护理周期完成</DialogTitle>
          </div>
          <DialogDescription>
            您已完成 {careDurationDays} 天的护理周期，请告诉我们最终结果
          </DialogDescription>
        </DialogHeader>

        {/* 结果选择 */}
        <div className="space-y-2 py-4">
          {outcomeOptions.map((option) => {
            const Icon = option.icon;
            const isSelected = selectedOutcome === option.value;

            return (
              <button
                key={option.value}
                type="button"
                onClick={() => setSelectedOutcome(option.value)}
                disabled={isSubmitting}
                className={cn(
                  "w-full flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left",
                  "hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
                  isSelected
                    ? `${option.bgColor} border-current ${option.color}`
                    : "border-gray-200 hover:border-gray-300"
                )}
              >
                <div
                  className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center",
                    isSelected ? option.bgColor : "bg-gray-100"
                  )}
                >
                  <Icon
                    className={cn(
                      "w-5 h-5",
                      isSelected ? option.color : "text-gray-400"
                    )}
                  />
                </div>
                <div>
                  <p
                    className={cn(
                      "font-medium",
                      isSelected ? option.color : "text-gray-900"
                    )}
                  >
                    {option.label}
                  </p>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* 反馈输入 */}
        {selectedOutcome && (
          <div className="space-y-2">
            {!showFeedback ? (
              <button
                type="button"
                onClick={() => setShowFeedback(true)}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                + 添加反馈（可选）
              </button>
            ) : (
              <Textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="分享您的护理经验或建议..."
                rows={3}
                className="resize-none"
                disabled={isSubmitting}
              />
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            稍后再说
          </Button>
          <Button
            onClick={handleComplete}
            disabled={!selectedOutcome || isSubmitting}
          >
            {isSubmitting ? "提交中..." : "完成护理"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CareCompletionModal;
